<template>
  <div
    ref="inputWrapper"
    class="px-2 py-4.5 w-full flex items-center rounded-lg relative border border-transparent z-10 transition-all duration-200 min-h-14"
    :class="{'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    style="box-shadow: 0 5px 10px rgba(31, 31, 31, 0.12)"
    @click="$refs.input.focus()"
    :style="(disabled ? {pointerEvents: 'none'} : {}, {marginBottom: errHeight + 'px'})"
  >
    <span
      ref="label"
      class="top-4.5 text-16 font-normal absolute z-20 leading-5 text-gray transition-all duration-200 pointer-events-none"
      :class="{[labelClass]: true, 'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    >
      {{ label }}
    </span>
    <div
      v-if="tagMode"
      ref="tags"
      class="bottom-4.5 text-16 absolute leading-5 text-black overflow-x-scroll flex items-center scrollbar-hidden"
      style="max-width: calc(100% - 1rem)"
      :class="{
        'text-gray': disabled,
        'pointer-events-none input-btn': actAsButton,
        'bg-white': theme === 'light',
        'bg-silver': theme === 'dark'
      }"
    >
      <div
        v-for="(tag, idx) in tags"
        :key="`tag_${idx}`"
        class="flex items-center gap-1 px-2 mr-2 font-semibold rounded-lg cursor-pointer select-none text-granite bg-concrete md:bg-white whitespace-nowrap py-px2"
        @click="tags.splice(idx, 1)"
      >
        <t-text size="sm">{{ tag }}</t-text>
        <ph-x size="14" />
      </div>
      <input
        ref="input"
        :disabled="disabled || actAsButton"
        v-model="inputVal"
        @input="input()"
        @focus="focus()"
        @blur="blur()"
        @change="change"
        @keyup="keyup"
        :type="inputType"
        :min="min"
        :max="max"
        :step="step"
        :inputmode="inputmode"
        :autocomplete="autocomplete"
        enterkeyhint="done"
        class="w-full h-full"
        :class="{
          'text-gray': disabled,
          'pointer-events-none input-btn text-white': actAsButton,
          'bg-white': theme === 'light',
          'bg-silver': theme === 'dark'
        }"
      />
    </div>
    <input
      v-else
      ref="input"
      :disabled="disabled || actAsButton"
      v-model="inputVal"
      :value="value ? value : ''"
      @input="input()"
      @focus="focus()"
      @blur="blur()"
      @change="change"
      :type="inputType"
      :min="min"
      :max="max"
      :step="step"
      :inputmode="inputmode"
      :autocomplete="autocomplete"
      class="bottom-4.5 text-16 absolute leading-5 text-black"
      :class="{
        'text-gray': disabled,
        'pointer-events-none input-btn': actAsButton,
        'bg-white': theme === 'light',
        'bg-silver': theme === 'dark'
      }"
    />
    <div ref="rightSide" class="absolute text-black cursor-pointer right-2 bottom-4">
      <slot name="right-side"></slot>
    </div>
    <div
      ref="dropdown"
      v-if="dropDownMode"
      class="absolute top-[60px] inset-x-2 max-h-[200px] min-h-[50px] rounded-lg flex-col p-2 overflow-y-scroll overflow-x-hidden hidden opacity-0 translate-y-5 duration-200 z-max"
      :class="{'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
      style="box-shadow: 0 5px 10px rgba(31, 31, 31, 0.12)"
    >
      <div
        v-for="(item, idx) in dropdown.suggestions"
        v-show="!dropdown.searching"
        :key="`suggestion_${idx}`"
        @click.stop="applyDropDownData(item)"
        class="items-center px-3 py-3 -ml-2 -mr-2 truncate duration-200 cursor-pointer hover:bg-concrete/70 min-h-11 max-h-11 h-11"
        style="display: flex"
      >
        <t-text size="md" color="black">{{ item }}</t-text>
      </div>
      <div
        v-html="loadingCircleSVG"
        v-if="dropdown.searching && dropdown.suggestions.length !== 0"
        class="items-center justify-center w-full h-10"
      />
      <t-text size="md" color="gray" class="m-auto" v-show="dropdown.suggestions.length === 0">No suggestions yet...</t-text>
    </div>
    <t-text
      :size="'md'"
      v-show="handleErrors"
      ref="error"
      class="absolute left-0 ml-2 break-word"
      :style="{bottom: `-${errHeight}px`}"
      color="red"
      >{{ fieldError }}</t-text
    >
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import AXIOS from '@/integrations/axios'
  export default {
    name: 'default-input',
    props: {
      label: {
        type: String,
        required: true
      },
      pattern: {
        type: String,
        default: ''
      },
      actAsButton: {
        type: Boolean,
        default: false
      },
      tagMode: {
        type: Boolean,
        default: false
      },
      labelClass: String,
      inputType: String,
      autocomplete: String,
      dropDownMode: {
        type: Boolean,
        default: false
      },
      dropDownDataUrl: {
        type: String,
        default: ''
      },
      value: [String, Number, Array],
      min: [String, Number],
      max: [String, Number],
      step: [String, Number],
      handleErrors: {
        type: Boolean,
        required: false,
        default: true
      },
      disabled: Boolean,
      preventAutoFocus: Boolean,
      inputmode: {
        type: String,
        validator: v => ['none', 'numeric', 'tel', 'decimal', 'email', 'url', 'search'].indexOf(v) !== -1
      }
    },
    data() {
      return {
        inputVal: this.tagMode ? this.value || '' : Array.isArray(this.value) ? this.value.join(', ') : this.value || '',
        interval: null,
        fieldError: '',
        errHeight: 0,
        inputColor: null,
        isValid: true,
        dropdown: {
          searching: false,
          suggestions: []
        },
        tags: []
      }
    },
    async mounted() {
      if (this.tagMode) this.inputVal = ''
      if (this.tagMode && this.value !== '' && this.value !== null) this.tags = this.value
      this.inputColor = this.theme === 'light' ? 'rgb(255, 255, 255)' : 'rgb(79, 79, 79)'
      await this.componentLoaded()
      if (this.dropDownMode) {
        this.debounceDropDownSearch = this.$helpers.debounce(() => this.handleDropDownSearch(), 500)
        this.fetchDropDownData()
      }
      this.$refs.input.style.width = `calc(100% - ${this.$refs.rightSide.clientWidth}px - 1.5rem`
      this.$refs.input.style.minWidth = `calc(100% - ${this.$refs.rightSide.clientWidth}px - 1.5rem`
      if (this.inputType === 'number') {
        this.$refs.input.addEventListener('keypress', e => this.secureNumberInput(e))
      }

      if (!this.preventAutoFocus && (this.$refs.input.value !== '' || (this.tagMode && this.tags.length !== 0))) this.focus(true)

      setTimeout(() => {
        if (!this.preventAutoFocus && (this.$refs.input.value !== '' || (this.tagMode && this.tags.length !== 0))) this.focus(true)
      }, 100)

      this.interval = setInterval(() => {
        let style = window.getComputedStyle(this.$refs.input)

        if (style && (style.backgroundColor !== this.inputColor || style.backgroundImage !== 'none')) {
          this.$emit('isAutofilled')
          clearInterval(this.interval)
        }
      }, 333)
    },
    methods: {
      secureNumberInput(e) {
        if ((e.which !== 8 && e.which !== 0 && e.which !== 13 && e.which < 48) || e.which > 57) {
          e.preventDefault()
        }
      },
      async componentLoaded() {
        return new Promise(resolve => {
          const interval = setInterval(() => {
            if (this.$refs.input && this.$refs.inputWrapper && this.$refs.label && this.$refs.rightSide && this.$refs.error) {
              clearInterval(interval)
              resolve()
            }
          }, 10)
        })
      },
      async fetchDropDownData() {
        if (this.dropDownDataUrl === '') return console.error('Invalid dropdown fetching url in default-input component')
        const res = await AXIOS.get(this.dropDownDataUrl)
        this.dropdown.suggestions = res.data
      },
      async handleDropDownSearch() {
        if (this.dropDownDataUrl === '') return console.error('Invalid dropdown fetching url in default-input component')
        this.dropdown.searching = true
        const res = await AXIOS.get(this.dropDownDataUrl + '&search=' + this.inputVal)
        this.dropdown.suggestions = res.data
        setTimeout(() => {
          this.dropdown.searching = false
        }, 500)
      },
      applyDropDownData(value) {
        setTimeout(() => {
          if (this.tagMode) {
            this.inputVal = ''
            this.tags.push(value)
            this.$refs.input.focus()
          } else this.inputVal = value
          if (this.label) {
            this.$refs.inputWrapper.classList.replace(['px-2', 'py-4.5'], ['p-2'])
            this.$refs.label.classList.replace(
              ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal'],
              ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold']
            )
            this.$refs.input.classList.replace('bottom-4.5', 'bottom-2')
          }
        }, 100)
      },
      keyup(e) {
        const inputValue = e.target.value
        const lastLetter = inputValue.charAt(inputValue.length - 1)
        if (lastLetter === ',' || e.keyCode === 13) {
          this.tags.push(this.inputVal.replace(/,/g, ''))
          this.inputVal = ''
          setTimeout(() => {
            this.$refs.tags.scrollLeft = this.$refs.tags.scrollLeftMax
          }, 100)
        }
      },
      input() {
        if (this.pattern !== '') {
          const regex = new RegExp(this.pattern.replace(/'/g, ''))
          if (!regex.test(this.getValue())) {
            this.isValid = false
          } else {
            if (this.isValid) {
              this.$refs.inputWrapper.classList.replace(['border-red', 'border-transparent'], 'border-green')
              this.$refs.label.classList.replace(['text-red', 'text-gray'], 'text-green')
            }
            this.clearError()
          }
        }

        if (this.dropDownMode) {
          this.openDropDown()
          this.debounceDropDownSearch()
        }
        if (!this.tagMode) this.$emit('input', this.getValue())
      },
      openDropDown() {
        if (this.dropDownMode) {
          this.$refs.dropdown.classList.replace('hidden', 'flex')
          this.$refs.inputWrapper.classList.replace('z-10', 'z-100')
          setTimeout(() => {
            this.$refs.dropdown.classList.replace(['opacity-0', 'translate-y-5'], ['opacity-100', 'translate-y-0'])
          }, 200)
        }
      },
      closeDropDown() {
        if (this.dropDownMode) {
          setTimeout(() => {
            this.$refs.dropdown.classList.replace(['opacity-100', 'translate-y-0'], ['opacity-0', 'translate-y-5'])
            setTimeout(() => {
              this.$refs.dropdown.classList.replace('flex', 'hidden')
              this.$refs.inputWrapper.classList.replace('z-100', 'z-10')
              this.$emit('dropDownClose')
            }, 200)
          }, 100)
        }
      },
      focus(init = false) {
        if (this.label) {
          this.$refs.inputWrapper.classList.replace(['px-2', 'py-4.5'], ['p-2'])
          this.$refs.label.classList.replace(
            ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal'],
            ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold']
          )
          if (!this.tagMode) this.$refs.input.classList.replace('bottom-4.5', 'bottom-2')
          else this.$refs.tags.classList.replace('bottom-4.5', 'bottom-2')
        }

        if (this.dropDownMode && !init) this.openDropDown()

        this.$emit('focus')
      },
      blur() {
        if (((this.inputVal.length === 0 && !this.tagMode) || (this.tagMode && this.tags.length === 0)) && this.label) {
          this.$refs.inputWrapper.classList.replace(['p-2'], ['px-2', 'py-4.5'])
          this.$refs.label.classList.replace(
            ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold'],
            ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal']
          )
          this.$refs.input.classList.replace('bottom-2', 'bottom-4.5')
        }

        if (this.dropDownMode) {
          this.closeDropDown()
        }
        this.$emit('blur')
      },
      change($event) {
        const value = $event.target.value

        if (this.inputType === 'number' && this.min !== null && this.max !== null && this.min >= 0 && this.max >= 0) {
          const notEnough = value < this.min
          const tooMuch = value > this.max
          const err = notEnough
            ? this.$t('components.filters.minValue', {val: this.min})
            : tooMuch
            ? this.$t('components.filters.maxValue', {val: this.max})
            : ''

          if (err) this.$toast.error(err)

          $event.target.value = notEnough ? +this.min : tooMuch ? +this.max : value
        } else {
          $event.target.value = value
        }

        this.$emit('change', $event.target.value)
      },
      getValue() {
        if (this.tagMode) return this.tags
        return Array.isArray(this.value) ? (this.value.length === 1 ? this.inputVal : this.value) : this.inputVal
      },
      setError(err) {
        const span = document.createElement('span')
        span.textContent = err
        span.style.cssText = 'word-break: break-word'
        const height = this.$helpers.getNodeHeight([span], this.$refs.input.clientWidth)
        this.$refs.error.$el.style.bottom = 0 - height + 'px'
        this.$refs.error.$el.style.maxWidth = this.$refs.input.clientWidth + 'px'
        this.$refs.inputWrapper.style.marginBottom = height + 'px'
        this.$refs.inputWrapper.classList.add('not-valid')
        this.fieldError = err
        setTimeout(() => {
          this.errHeight = this.$refs.error.$el.clientHeight + 8
        }, 0)
        this.isValid = false
      },
      hasError() {
        return this.fieldError !== ''
      },
      clearError() {
        this.fieldError = ''
        this.errHeight = 0
        this.$refs.inputWrapper.style.marginBottom = ''
        this.$refs.inputWrapper.classList.remove('not-valid')
        this.isValid = true
      }
    },
    watch: {
      value(newVal) {
        if (this.tagMode) {
          this.tags = newVal
        } else this.inputVal = Array.isArray(newVal) ? newVal.join(', ') : newVal || ''
        if (!this.preventAutoFocus && !this.disabled && (this.inputVal !== null || this.inputVal !== '')) this.focus()
        if (!this.disabled && (this.inputVal === null || this.inputVal === '')) this.blur()
      },
      isValid(newVal) {
        if (newVal) {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-red', 'border-transparent'], 'border-green')
            this.$refs.label.classList.replace(['text-red', 'text-gray'], 'text-green')
          }, 0)
        } else {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-green', 'border-transparent'], 'border-red')
            this.$refs.label.classList.replace(['text-gray', 'text-green'], 'text-red')
          }, 0)
        }
      }
    },
    computed: {
      ...mapGetters(['themeColorHEX', 'theme']),
      loadingCircleSVG() {
        let fill = this.$helpers.getCorrectColor(this.themeColorHEX)
        return `<svg
                  style="width: 100%; height: 100%"
                  id="loader"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  xmlns:xlink="http://www.w3.org/1999/xlink"
                  x="0px"
                  y="0px"
                  viewBox="0 0 100 100"
                  enable-background="new 0 0 0 0"
                  xml:space="preserve"
                >
                  <path fill="${fill}" d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
                    <animateTransform
                      attributeName="transform"
                      attributeType="XML"
                      type="rotate"
                      dur="1s"
                      from="0 50 50"
                      to="360 50 50"
                      repeatCount="indefinite"
                    />
                  </path>
                </svg>
        `
      }
    },
    beforeDestroy() {
      if (this.inputType === 'number') {
        this.$refs.input.removeEventListener('keypress', e => this.secureNumberInput(e))
      }
    },
    destroyed() {
      clearInterval(this.interval)
    }
  }
</script>
